// ---------------------------------------------------------------------------
// TextField and Select, ported from the design system (R8)
//
// Both sit in the system's field: a label (always; placeholder text is not a
// label), the control, and one of a hint or an error — the error as the fix,
// with the alert icon, the control marked invalid and described by it.
// `size: 'sm'` (28px) fits a field into a table row or a filter bar.
//
// The control's id is the caller's, or one of the field's own; the label and
// the message point at it. A controlled field gets `value` and onInput or
// onChange; `defaultValue` only sets where it starts. A Select's option is
// marked selected as the system marks it, and held selected by property, so
// a controlled Select follows its value after the user has changed it too.
//
// A controlled TextField's value is the input's value property, as React
// holds it (R10d): written after the other attributes and before the value
// attribute, as React writes it when it mounts the input, so the field
// starts with its caret after the text (a Popover or Dialog that focuses it
// gets "3009" from a 9 typed into "300", not "9300"), and again whenever the
// field shows something else, typed or not. "Something else" as React
// compares (heldValue): a number field holding a number by its number, so
// "1.0" stays while the page holds 1 (R10d review). An uncontrolled field's
// defaultValue goes to the property once, when it mounts, as React writes
// it: its caret starts after the text too. The value attribute stays: it is
// what the system's server render writes.
// ---------------------------------------------------------------------------

import { html, noChange, type TemplateResult } from 'lit';
import { Directive, directive, type PartInfo, PartType, type PropertyPart } from 'lit/directive.js';
import { setCommittedValue } from 'lit/directive-helpers.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { ref } from 'lit/directives/ref.js';

import { cx, node, present } from './helpers.js';
import { type IconName, icon } from './icons.js';

let fields = 0;

/** An id for a field whose caller passed none. */
export function fieldId(): string {
  return `sdr-field-${++fields}`;
}

interface FieldFrame {
  id: string;
  label?: unknown;
  hint?: unknown;
  error?: unknown;
  optional?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

function field(p: FieldFrame, control: TemplateResult): TemplateResult {
  const message = present(p.error)
    ? html`<div class="sdr-field__error" id=${`${p.id}-msg`}>${icon({ name: 'alert', size: 12 })}${node(p.error)}</div>`
    : present(p.hint)
      ? html`<div class="sdr-field__hint" id=${`${p.id}-msg`}>${node(p.hint)}</div>`
      : null;
  return html`<div
    class=${cx('sdr-field', p.size === 'sm' && 'sdr-field--sm', present(p.error) && 'sdr-field--invalid', p.className)}
  >
    ${
      present(p.label)
        ? html`<label class="sdr-field__label" for=${p.id}
          >${node(p.label)}${p.optional ? html`<span class="sdr-field__opt"> optional</span>` : null}</label
        >`
        : null
    }
    ${control}
    ${message}
  </div>`;
}

const describedBy = (p: FieldFrame) => (present(p.error) || present(p.hint) ? `${p.id}-msg` : undefined);

export interface TextFieldProps extends Omit<FieldFrame, 'id'> {
  id?: string;
  /** search for search boxes. */
  icon?: IconName;
  /** A unit after the value ("per day"). */
  suffix?: string;
  type?: string;
  name?: string;
  value?: string | number;
  defaultValue?: string | number;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  min?: string | number;
  max?: string | number;
  step?: string | number;
  maxLength?: number;
  pattern?: string;
  autocomplete?: string;
  inputMode?: string;
  ariaLabel?: string;
  onInput?: (event: InputEvent) => void;
  onChange?: (event: Event) => void;
  onKeydown?: (event: KeyboardEvent) => void;
  onBlur?: (event: FocusEvent) => void;
}

const text = (v: string | number | undefined) => (v == null ? undefined : String(v));

/**
 * An input's value property as ReactDOMInput writes it (R10d review). Controlled: whenever the field shows something
 * else, compared as React compares: a number field holding a number by number (React's node.value != value), so
 * "1.0" stays while the page holds 1 and the person types on to "1.05", and 0 is written over an empty field;
 * anything else as text, as live() does. Compared as text, "1" went over "1.0": typing 1.05 gave "15" in Chrome.
 * Uncontrolled: the defaultValue once, when the field mounts (postMountWrapper), never again: in Chrome that write
 * puts the caret after the text, so a Dialog that focuses the field gets "3009" from a 9, not "9300". On the server
 * only render() runs: a controlled value, which @lit-labs/ssr writes as the value attribute.
 */
class HeldValue extends Directive {
  private mounted = false;

  constructor(info: PartInfo) {
    super(info);
    if (info.type !== PartType.PROPERTY) throw new Error('heldValue() binds .value');
  }

  render(value: string | number | undefined, _type?: string, _defaultValue?: string | number) {
    return value == null ? noChange : String(value);
  }

  override update(
    part: PropertyPart,
    [given, type, defaultValue]: [string | number | undefined, string | undefined, string | number | undefined],
  ) {
    const mounting = !this.mounted;
    this.mounted = true;
    const value = given != null ? given : mounting ? defaultValue : undefined;
    if (value == null) return noChange;
    const shown = (part.element as HTMLInputElement).value;
    const same =
      type === 'number' && typeof value === 'number'
        ? !(value === 0 && shown === '') && Number(shown) === value
        : shown === String(value);
    if (same) return noChange;
    // Written even when Lit committed the same text before: the person may have typed something else since.
    setCommittedValue(part);
    return String(value);
  }
}
const heldValue = directive(HeldValue);

export function textField(p: TextFieldProps): TemplateResult {
  const frame = { ...p, id: p.id || fieldId() };
  return field(
    frame,
    html`<div class="sdr-field__control">
      ${p.icon ? icon({ name: p.icon, size: 16, className: 'sdr-field__icon' }) : null}<input
        id=${frame.id}
        class=${cx('sdr-input', p.size === 'sm' && 'sdr-input--sm', p.icon && 'sdr-input--icon', p.suffix && 'sdr-input--suffix')}
        type=${ifDefined(p.type)}
        name=${ifDefined(p.name)}
        placeholder=${ifDefined(p.placeholder)}
        ?disabled=${p.disabled}
        ?readonly=${p.readOnly}
        ?required=${p.required}
        min=${ifDefined(text(p.min))}
        max=${ifDefined(text(p.max))}
        step=${ifDefined(text(p.step))}
        maxlength=${ifDefined(p.maxLength)}
        pattern=${ifDefined(p.pattern)}
        autocomplete=${ifDefined(p.autocomplete)}
        inputmode=${ifDefined(p.inputMode)}
        aria-label=${ifDefined(p.ariaLabel)}
        aria-invalid=${ifDefined(present(p.error) ? 'true' : undefined)}
        aria-describedby=${ifDefined(describedBy(frame))}
        .value=${heldValue(p.value, p.type, p.defaultValue)}
        value=${ifDefined(text(p.value !== undefined ? p.value : p.defaultValue))}
        @input=${p.onInput}
        @change=${p.onChange}
        @keydown=${p.onKeydown}
        @blur=${p.onBlur}
      />${p.suffix ? html`<span class="sdr-field__suffix">${p.suffix}</span>` : null}
    </div>`,
  );
}

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<FieldFrame, 'id'> {
  id?: string;
  options: Array<SelectOption | string>;
  value?: string;
  defaultValue?: string;
  name?: string;
  disabled?: boolean;
  required?: boolean;
  ariaLabel?: string;
  onChange?: (event: Event) => void;
}

export function select(p: SelectProps): TemplateResult {
  const frame = { ...p, id: p.id || fieldId() };
  const options = (p.options || []).map(o => (typeof o === 'string' ? { value: o, label: o } : o));
  const chosen = p.value !== undefined ? p.value : p.defaultValue;
  // A controlled select holds its value by property; the attribute alone stops counting once the user has picked.
  const hold = (on: boolean) =>
    p.value !== undefined
      ? ref(el => {
          if (el) (el as HTMLOptionElement).selected = on;
        })
      : null;
  return field(
    frame,
    html`<div class="sdr-field__control">
      <select
        id=${frame.id}
        class=${cx('sdr-select', p.size === 'sm' && 'sdr-select--sm')}
        name=${ifDefined(p.name)}
        ?disabled=${p.disabled}
        ?required=${p.required}
        aria-label=${ifDefined(p.ariaLabel)}
        aria-invalid=${ifDefined(present(p.error) ? 'true' : undefined)}
        aria-describedby=${ifDefined(describedBy(frame))}
        @change=${p.onChange}
      >
        ${options.map(
          o =>
            html`<option value=${o.value} ?disabled=${o.disabled} ?selected=${o.value === chosen} ${hold(o.value === chosen)}
              >${o.label}</option
            >`,
        )}
      </select>
      ${icon({ name: 'chevron-down', size: 16, className: 'sdr-select__chev' })}
    </div>`,
  );
}
